import styled from '@emotion/styled'
import {css, keyframes} from '@emotion/react'
import {theme} from '../../theme'

// ─────────────────────────────────────────────
// Outer wrapper
// ─────────────────────────────────────────────

export const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.space[1]};
  width: 100%;
  position: relative;
`

// ─────────────────────────────────────────────
// Label
// ─────────────────────────────────────────────

export const LabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

export const LabelEl = styled.label`
  display: block;
  font-size: ${({variant}) =>
    variant === 'form' ? '12px' : theme.fontSizes.md};
  font-weight: ${theme.fontWeights.medium};
  color: ${theme.colors.text.primary};
`

export const LabelReq = styled.span`
  font-size: 12px;
  font-weight: ${theme.fontWeights.medium};
  color: ${theme.colors.status.danger.text};
`

// ─────────────────────────────────────────────
// Field row — pill that wraps input + button
// ─────────────────────────────────────────────

export const FieldRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;

  background: #ffffff;
  border: 1px solid
    ${({hasError}) =>
      hasError ? '#d92d20' : `${theme.colors.border.focusSubtle}`};
  border-radius: ${theme.radii.md2};
  overflow: hidden;
  transition: border-color 150ms ease, box-shadow 150ms ease;
  padding: ${theme.space['0.5']};

  opacity: ${({disabled}) => (disabled ? 0.55 : 1)};
  pointer-events: ${({disabled}) => (disabled ? 'none' : 'auto')};

  &:focus-within {
    border-color: ${({hasError}) => (hasError ? '#d92d20' : '#4166e8')};
    box-shadow: 0 0 0 3px
      ${({hasError}) =>
        hasError ? 'rgba(217,45,32,0.12)' : 'rgba(65,102,232,0.15)'};
  }
`

// ─────────────────────────────────────────────
// Left side: input + inline icon buttons
// ─────────────────────────────────────────────

export const InputWrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  min-width: 0;
  position: relative;
`

export const StyledInput = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  color: ${theme.colors.input.text};
  font-size: 14px;

  padding: ${({size}) => {
    switch (size) {
      case 'sm':
        return '0 10px'
      case 'lg':
        return '0 16px'
      default:
        return '0 12px'
    }
  }};

  height: ${({size}) => {
    switch (size) {
      case 'sm':
        return '32px'
      case 'lg':
        return '46px'
      default:
        return '38px'
    }
  }};

  &::placeholder {
    color: ${theme.colors.input.placeholder};
  }
`

// ─────────────────────────────────────────────
// Spinner
// ─────────────────────────────────────────────

const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`

export const SpinnerIcon = styled.span`
  flex-shrink: 0;
  width: 14px;
  height: 14px;
  margin-right: 10px;
  border: 2px solid #e5e7eb;
  border-top-color: #4166e8;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
`

// ─────────────────────────────────────────────
// Clear button (×)
// ─────────────────────────────────────────────

export const ClearButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  width: 20px;
  height: 20px;
  margin-right: 4px;
  border-radius: 50%;
  border: none;
  background: #e5e7eb;
  color: #6a7181;
  cursor: pointer;
  padding: 0;

  &:hover {
    background: #d1d5db;
    color: #374151;
  }

  &:focus-visible {
    outline: 2px solid #4166e8;
    outline-offset: 1px;
  }
`

// ─────────────────────────────────────────────
// Chevron toggle
// ─────────────────────────────────────────────

export const ChevronButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  width: 32px;
  height: 100%;
  border: none;
  background: transparent;
  color: #9ca3af;
  cursor: pointer;
  padding: 0;
`

// ─────────────────────────────────────────────
// Divider between input and action button
// ─────────────────────────────────────────────

export const Divider = styled.span`
  flex-shrink: 0;
  width: 1px;
  height: 24px;
  background: #e5e7eb;
`

// ─────────────────────────────────────────────
// Action button
// ─────────────────────────────────────────────

const actionVariantStyles = ({actionVariant = 'primary'}) => {
  switch (actionVariant) {
    case 'secondary':
      return css`
        background: transparent;
        color: #4166e8;
        &:hover:not(:disabled) {
          background: #f0f3ff;
        }
      `
    case 'ghost':
      return css`
        background: transparent;
        color: #6a7181;
        &:hover:not(:disabled) {
          background: #f3f4f6;
        }
      `
    case 'primary':
    default:
      return css`
        background: #ffffff;
        color: ${theme.colors.text.linkStrong};
        border: 1px solid ${theme.colors.border.focusStrong};
        border-radius: ${theme.radii.sm2};
        padding: ${theme.space['2.5']};
        &:hover:not(:disabled) {
          background: #f0f3ff;
        }
      `
  }
}

export const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;

  height: 100%;
  padding: 0 14px;

  border: none;
  border-radius: 3px;
  cursor: pointer;

  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;

  transition: background 150ms ease, color 150ms ease;

  ${actionVariantStyles}

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid #4166e8;
    outline-offset: -2px;
  }
`

export const ActionButtonIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;

  svg {
    width: 16px;
    height: 16px;
  }
`

export const ActionButtonLabel = styled.span``

// ─────────────────────────────────────────────
// Dropdown
// ─────────────────────────────────────────────

export const DropdownList = styled.ul`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 999;

  list-style: none;
  margin: 0;
  padding: 4px 0;

  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

  max-height: 220px;
  overflow-y: auto;
`

export const DropdownItem = styled.li`
  display: flex;
  align-items: center;
  gap: 8px;

  padding: 8px 12px;

  font-size: 14px;
  color: ${({isSelected}) => (isSelected ? '#4166e8' : '#1f2937')};
  background: ${({isSelected}) => (isSelected ? '#f0f3ff' : 'transparent')};
  font-weight: ${({isSelected}) => (isSelected ? '600' : '400')};

  cursor: pointer;
  user-select: none;

  &:hover {
    background: #f3f4f6;
    color: #1f2937;
  }

  &:focus-visible {
    outline: 2px solid #4166e8;
    outline-offset: -2px;
  }
`

export const DropdownEmpty = styled.li`
  padding: 10px 12px;
  font-size: 14px;
  color: ${({isError}) => (isError ? '#d92d20' : '#9ca3af')};
  text-align: center;
  list-style: none;
`

export const DropdownLoading = styled.li`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 12px;
  font-size: 14px;
  color: #9ca3af;
  list-style: none;
`

// ─────────────────────────────────────────────
// Helper / error text
// ─────────────────────────────────────────────

export const HelperText = styled.p`
  margin: 0;
  font-size: ${theme.fontSizes.sm};
  color: ${({isError}) =>
    isError ? theme.colors.status.danger.text : theme.colors.text.muted};
`
